import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, X, Sparkles, Gift, Clock, AlertTriangle, Trash2, Send, Users, MessageSquare, Briefcase, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useNativeShare } from "@/hooks/useNativeShare";
import { getExtendablePromo, graceDeadline } from "@/utils/promoState";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TempPromoModal — Admin creates a TEMPORARY discount promotion for a
 * hechicería/pacto plan. The client receives a PNG "invitation" in their
 * chat worded as if the system chose them, and the admin immediately gets
 * a WhatsApp share panel to forward the same message via WhatsApp
 * (regular) or WhatsApp Business.
 *
 * Rules displayed to admin (NEVER shown to the client):
 *  - Client must pay the discounted total within valid_hours to liquidate.
 *  - Partial payments are recorded normally, no discount applied.
 *  - Only ONE active promo per plan at a time.
 */
export const TempPromoModal = ({ plan, clientUser = null, authHeader, onClose }) => {
  const [kind, setKind] = useState("percentage");
  const [value, setValue] = useState("");
  const [validHours, setValidHours] = useState("48");
  const [customMessage, setCustomMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [existing, setExisting] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  // After a successful create, we surface the WhatsApp share panel using
  // the promo message the backend generated (single source of truth).
  const [shareData, setShareData] = useState(null);

  useEffect(() => {
    if (!plan?.id) return;
    setLoadingExisting(true);
    axios.get(`${API}/admin/payment-plans/${plan.id}/promotions`, { headers: authHeader() })
      .then((r) => setExisting(Array.isArray(r.data) ? r.data : []))
      .catch(() => setExisting([]))
      .finally(() => setLoadingExisting(false));
  }, [plan?.id]);

  // Close on Escape for keyboard-first admins
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  if (!plan) return null;

  const currency = plan.currency || "MXN";
  const agreed = Number(plan.agreed_price_after_offer ?? plan.agreed_price ?? 0);
  const paid = Number(plan.paid_total ?? 0);
  const remainingBefore = Math.max(0, agreed - paid);

  // Preview the calculated new total based on inputs
  const numVal = Number(value || 0);
  const discountAmount = kind === "amount"
    ? Math.min(numVal, remainingBefore - 0.01)
    : Math.min((remainingBefore * numVal) / 100, remainingBefore - 0.01);
  const newRemaining = Math.max(0, remainingBefore - Math.max(0, discountAmount));

  const activePromo = existing.find((p) => p.status === "active" && new Date(p.valid_until).getTime() > Date.now());
  const recentlyExpired = (() => { const e = getExtendablePromo({ promotions: existing }); return e?.isRecentlyExpired ? e.promo : null; })();

  const submit = async () => {
    setError("");
    if (!value || numVal <= 0) return setError("Ingresa un descuento válido");
    if (kind === "percentage" && numVal > 99) return setError("Máximo 99%");
    if (kind === "amount" && numVal > remainingBefore) return setError(`Máximo ${remainingBefore.toFixed(2)} ${currency}`);
    const hours = parseInt(validHours, 10);
    if (!hours || hours < 1 || hours > 720) return setError("Validez entre 1 y 720 horas");

    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/payment-plans/${plan.id}/promotions`,
        {
          kind,
          value: numVal,
          valid_hours: hours,
          ...(customMessage.trim() ? { custom_message: customMessage.trim() } : {}),
        },
        { headers: authHeader() },
      );
      toast.success("Promoción enviada al chat del cliente");
      const promo = r.data.promotion;
      setExisting((prev) => [...prev, promo]);
      setValue("");
      // Build share data (single source of truth: backend chat_message)
      const backend = process.env.REACT_APP_BACKEND_URL;
      const shareUrl = `${backend}${r.data.share_path}`;
      // Prepend a heading and append the share URL so WhatsApp shows the
      // link preview with a rich card.
      const chatMsg = promo.chat_message
        || `¡Hola! El sistema activó una promoción especial para ti. Consulta los detalles: ${shareUrl}`;
      const fullMsg = `${chatMsg}\n\n${shareUrl}`;
      setShareData({
        message: fullMsg,
        share_url: shareUrl,
        image_url: `${backend}${r.data.image_path}`,
        client_name: clientUser?.name || clientUser?.username || plan.user_name || "cliente",
        client_phone: clientUser?.phone || plan.user_phone || "",
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo crear la promoción");
    } finally {
      setBusy(false);
    }
  };

  const cancelActive = async (promoId) => {
    if (!window.confirm("¿Cancelar esta promoción? El cliente ya recibió la imagen — puedes crear otra después.")) return;
    try {
      await axios.delete(
        `${API}/admin/payment-plans/${plan.id}/promotions/${promoId}`,
        { headers: authHeader() },
      );
      setExisting((prev) => prev.map((p) => p.id === promoId ? { ...p, status: "cancelled" } : p));
      toast.success("Promoción cancelada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cancelar");
    }
  };

  const fmtMoney = (n) => `${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
  const fmtLocal = (iso) => {
    try {
      return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[150] bg-black/85 backdrop-blur-md flex items-center justify-center px-3 py-6 overflow-y-auto"
        data-testid="temp-promo-modal"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }}
          className="relative w-full max-w-lg bg-gradient-to-br from-[#0a0510] to-black border-2 border-[#d4af37]/60 rounded-2xl p-5 my-auto shadow-[0_0_60px_rgba(212,175,55,0.35)]"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            data-testid="temp-promo-close"
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>

          {/* When we have shareData, render the WhatsApp share panel INSTEAD
              of the promo creation form. Same modal, different content. */}
          {shareData ? (
            <PromoShareSubpanel data={shareData} onClose={onClose} />
          ) : (
            <>
          <div className="flex items-center gap-2 mb-1">
            <Gift className="w-5 h-5 text-[#d4af37]" />
            <h3 className="font-serif text-lg text-white">Crear Promoción Temporal</h3>
          </div>
          <p className="text-xs text-neutral-400 mb-3">
            El cliente recibirá una imagen tipo "¡Fuiste elegid@!" con los detalles y el nuevo total.
            El sistema le indica que la validez es limitada — <b className="text-white">nunca se menciona</b> que tú elegiste el tiempo.
          </p>

          {/* Plan snapshot */}
          <div className="rounded-lg bg-black/40 border border-white/10 p-3 mb-3 text-xs">
            <div className="text-neutral-400">Cliente: <b className="text-white">{plan.user_name}</b></div>
            <div className="text-neutral-400">Trabajo: <b className="text-white">{plan.service_name}</b></div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
              <div><div className="text-neutral-500 uppercase text-[9px]">Precio</div><div className="text-white">{fmtMoney(agreed)}</div></div>
              <div><div className="text-neutral-500 uppercase text-[9px]">Pagado</div><div className="text-emerald-300">{fmtMoney(paid)}</div></div>
              <div><div className="text-neutral-500 uppercase text-[9px]">Resta</div><div className="text-amber-300">{fmtMoney(remainingBefore)}</div></div>
            </div>
          </div>

          {/* Existing active promo */}
          {activePromo && (
            <div className="mb-3 rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 text-[11px]">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-amber-200 font-semibold">Ya hay una promoción activa</div>
                  <div className="text-neutral-300 mt-0.5">
                    {activePromo.kind === "percentage" ? `${activePromo.value}%` : fmtMoney(activePromo.value)}
                    {" · "}Ahorra {fmtMoney(activePromo.discount_amount)} · Válida hasta {fmtLocal(activePromo.valid_until)}
                    {Number(activePromo.extended_days_total || 0) > 0 && (
                      <span className="text-sky-300"> · prorrogada +{activePromo.extended_days_total} día{activePromo.extended_days_total !== 1 ? "s" : ""}</span>
                    )}
                  </div>
                  <button
                    onClick={() => cancelActive(activePromo.id)}
                    data-testid={`temp-promo-cancel-${activePromo.id}`}
                    className="mt-2 inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-red-400/40 text-red-300 hover:bg-red-500/10 uppercase tracking-widest"
                  >
                    <Trash2 className="w-3 h-3" /> Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Recently expired promo — can still be reactivated (≤3 days) */}
          {!activePromo && recentlyExpired && (
            <div data-testid="temp-promo-expired-hint" className="mb-3 rounded-lg border border-rose-400/40 bg-rose-500/10 p-3 text-[11px]">
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-rose-300 mt-0.5 flex-shrink-0" />
                <div className="flex-1 text-neutral-300">
                  <div className="text-rose-200 font-semibold">La promoción anterior venció {fmtLocal(recentlyExpired.valid_until)}</div>
                  Aún puedes <b className="text-white">reactivarla con una prórroga</b> (hasta {fmtLocal(graceDeadline(recentlyExpired).toISOString())}) usando el botón <b className="text-amber-200">"Reactivar / prorrogar"</b> de la tarjeta, o crear una promoción nueva aquí.
                </div>
              </div>
            </div>
          )}

          {!activePromo && (
            <>
              {/* Kind toggle */}
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-[#d4af37]/80 mb-1.5">Tipo de descuento</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="temp-promo-kind-pct"
                    onClick={() => setKind("percentage")}
                    className={`text-xs py-2 rounded border ${kind === "percentage" ? "bg-purple-500/20 border-purple-400/50 text-purple-200" : "bg-black/40 border-white/10 text-neutral-400"}`}
                  >
                    Porcentaje (%)
                  </button>
                  <button
                    type="button"
                    data-testid="temp-promo-kind-amount"
                    onClick={() => setKind("amount")}
                    className={`text-xs py-2 rounded border ${kind === "amount" ? "bg-emerald-500/20 border-emerald-400/50 text-emerald-200" : "bg-black/40 border-white/10 text-neutral-400"}`}
                  >
                    Monto fijo ({currency})
                  </button>
                </div>
              </div>

              {/* Value */}
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-[#d4af37]/80 mb-1.5">
                  {kind === "percentage" ? "% de descuento" : `Descuento en ${currency}`}
                </div>
                <input
                  data-testid="temp-promo-value"
                  type="number"
                  step={kind === "percentage" ? "1" : "10"}
                  min="0"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={kind === "percentage" ? "15" : "500"}
                  className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d4af37]"
                />
              </div>

              {/* Validity hours */}
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-[#d4af37]/80 mb-1.5 flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> Validez (horas)
                </div>
                <div className="flex gap-2 mb-2">
                  {[24, 48, 72, 168].map((h) => (
                    <button
                      key={h}
                      type="button"
                      data-testid={`temp-promo-quick-${h}`}
                      onClick={() => setValidHours(String(h))}
                      className={`text-[10px] px-2.5 py-1 rounded border ${String(validHours) === String(h) ? "bg-[#d4af37]/20 border-[#d4af37] text-[#d4af37]" : "bg-black/40 border-white/10 text-neutral-400"}`}
                    >
                      {h < 24 ? `${h}h` : h === 168 ? "7 días" : `${h / 24} días`}
                    </button>
                  ))}
                </div>
                <input
                  data-testid="temp-promo-hours"
                  type="number"
                  min="1"
                  max="720"
                  value={validHours}
                  onChange={(e) => setValidHours(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d4af37]"
                />
              </div>

              {/* Preview */}
              {numVal > 0 && discountAmount > 0 && (
                <div className="mb-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1">Vista previa del beneficio</div>
                  <div className="flex justify-between text-neutral-300"><span>Descuento:</span><b className="text-white">{fmtMoney(discountAmount)}</b></div>
                  <div className="flex justify-between text-neutral-300"><span>Nuevo total a pagar:</span><b className="text-emerald-300">{fmtMoney(newRemaining)}</b></div>
                </div>
              )}

              {/* Custom message (optional) */}
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1.5">Mensaje personalizado (opcional)</div>
                <textarea
                  data-testid="temp-promo-message"
                  rows={2}
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Si lo dejas vacío, se envía un mensaje predeterminado neutral."
                  className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-xs focus:outline-none focus:border-[#d4af37]"
                />
                <p className="text-[10px] text-neutral-500 mt-1">
                  No incluyas frases como "el admin decidió" — el cliente debe percibir la promo como enviada por el sistema.
                </p>
              </div>

              {error && (
                <div className="mb-3 text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2">{error}</div>
              )}

              <button
                type="button"
                data-testid="temp-promo-submit"
                disabled={busy}
                onClick={submit}
                className="btn-gold w-full py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest font-bold disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Enviar promoción al cliente
              </button>
            </>
          )}

          {/* History */}
          {existing.length > 0 && (
            <div className="mt-4 pt-3 border-t border-white/5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2">Historial ({existing.length})</div>
              <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                {[...existing].reverse().map((p) => (
                  <div key={p.id} className="text-[10px] flex justify-between items-center gap-2 bg-black/40 rounded px-2 py-1.5">
                    <div className="min-w-0 flex-1 truncate">
                      <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${p.status === "active" ? "bg-amber-400 animate-pulse" : p.status === "accepted" ? "bg-emerald-400" : "bg-neutral-600"}`} />
                      {p.kind === "percentage" ? `${p.value}%` : fmtMoney(p.value)} · {fmtMoney(p.discount_amount)}
                      <span className="text-neutral-500 ml-1">
                        · {p.status === "accepted" ? "liquidada ✓" : p.status === "cancelled" ? "cancelada" : p.status === "expired" ? "expirada" : "activa"}
                        {Number(p.extended_days_total || 0) > 0 && ` · +${p.extended_days_total}d prórroga`}
                      </span>
                    </div>
                    <div className="text-neutral-500 whitespace-nowrap">{fmtLocal(p.valid_until)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};


/**
 * PromoShareSubpanel — content displayed inside TempPromoModal after a promo
 * was successfully created. Offers 3 ways to forward the exact same message
 * the client received in their chat:
 *   - WhatsApp regular (whatsapp://send scheme + wa.me fallback)
 *   - WhatsApp Business (whatsapp-business://send scheme)
 *   - Pick contact (wa.me/?text=)
 * Plus a copy-link + download-image fallback.
 */
export const PromoShareSubpanel = ({ data, onClose, title = "Promoción activada", subtitle = "Ya se envió al chat del cliente. Compártela también por WhatsApp:" }) => {
  const { canShare, share } = useNativeShare();
  const digits = String(data.client_phone || "").replace(/\D/g, "");
  const encoded = encodeURIComponent(data.message);

  const nativeShare = async () => {
    const r = await share({
      url: data.share_url,
      text: data.message,
      title: `${title} · ${data.client_name} · CrisAngel Astral`,
      imageUrl: data.image_url,
    });
    if (r.ok) toast.success("Compartido");
  };

  // WhatsApp URL schemes:
  //   whatsapp://send?phone=&text=              → tries WA regular
  //   whatsapp-business://send?phone=&text=     → tries WA Business (Android)
  //   https://wa.me/{phone}?text=               → universal fallback (opens
  //                                                chooser between apps if
  //                                                both are installed on
  //                                                mobile; WA Web on desktop)
  const waRegular = digits
    ? `https://wa.me/${digits}?text=${encoded}`
    : `https://wa.me/?text=${encoded}`;
  const waBusiness = digits
    ? `whatsapp-business://send?phone=${digits}&text=${encoded}`
    : `whatsapp-business://send?text=${encoded}`;
  const waPickContact = `https://wa.me/?text=${encoded}`;

  const copyMsg = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(data.message);
      } else {
        throw new Error("clipboard unavailable");
      }
      toast.success("Mensaje copiado");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = data.message;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) toast.success("Mensaje copiado");
        else toast.error("Cópialo manualmente");
      } catch {
        toast.error("No se pudo copiar en este navegador");
      }
    }
  };

  return (
    <div data-testid="promo-share-subpanel">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-emerald-300" />
        <h3 className="font-serif text-lg text-white">{title}</h3>
      </div>
      <p className="text-xs text-neutral-400 mb-3">{subtitle}</p>

      {/* Image preview — same PNG the client got in chat */}
      <div className="mb-3 rounded-lg bg-black/40 border border-white/10 p-2 text-[11px]">
        <a href={data.share_url} target="_blank" rel="noreferrer" className="block group relative">
          <img
            src={data.image_url}
            alt="Vista previa promoción"
            className="w-full max-h-64 object-contain rounded transition group-hover:opacity-90"
            data-testid="promo-share-preview"
          />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/40 rounded">
            <span className="text-[10px] uppercase tracking-widest text-[#d4af37] bg-black/70 px-3 py-1 rounded-full">
              Abrir vista completa (con miniatura para WhatsApp)
            </span>
          </div>
        </a>
      </div>

      {/* Message preview (same as chat) */}
      <details className="mb-3 rounded bg-black/30 border border-white/5 p-2 text-[10px]">
        <summary className="cursor-pointer text-neutral-400 uppercase tracking-widest">
          Ver mensaje que se enviará
        </summary>
        <pre
          data-testid="promo-share-message"
          className="mt-2 whitespace-pre-wrap text-neutral-300 font-sans text-[11px] leading-snug max-h-40 overflow-y-auto"
        >{data.message}</pre>
      </details>

      <div className="space-y-2">
        {/* Native share — mobile-only. Opens OS share sheet in one tap. */}
        {canShare && (
          <button
            data-testid="promo-share-native"
            onClick={nativeShare}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#d4af37] hover:bg-[#f3d878] text-black font-bold text-xs uppercase tracking-widest"
            title="Abre el compartir del sistema (WhatsApp, Telegram, Messages, Email…)"
          >
            <Share2 className="w-3.5 h-3.5" />
            Compartir con app…
          </button>
        )}
        <a
          data-testid="promo-share-wa"
          href={waRegular}
          target="_blank"
          rel="noreferrer"
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs uppercase tracking-widest"
        >
          <Send className="w-3.5 h-3.5" />
          {digits ? `WhatsApp a ${data.client_name}` : "WhatsApp — elegir número"}
        </a>
        <a
          data-testid="promo-share-wa-business"
          href={waBusiness}
          target="_blank"
          rel="noreferrer"
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-xs uppercase tracking-widest"
          title="Abre WhatsApp Business si está instalado (móvil)"
        >
          <Briefcase className="w-3.5 h-3.5" />
          WhatsApp Business
        </a>
        <a
          data-testid="promo-share-wa-contact"
          href={waPickContact}
          target="_blank"
          rel="noreferrer"
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-white/15 text-neutral-200 hover:bg-white/5 text-xs"
        >
          <Users className="w-3.5 h-3.5" />
          Elegir contacto de WhatsApp
        </a>
        <button
          data-testid="promo-share-copy"
          onClick={copyMsg}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-white/15 text-neutral-400 hover:text-white hover:bg-white/5 text-xs"
        >
          <MessageSquare className="w-3.5 h-3.5" /> Copiar mensaje
        </button>
        <a
          data-testid="promo-share-download-image"
          href={data.image_url}
          download="promocion.png"
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-white/15 text-neutral-400 hover:text-white hover:bg-white/5 text-xs"
        >
          <Gift className="w-3.5 h-3.5" /> Descargar imagen
        </a>
      </div>

      <button
        data-testid="promo-share-done"
        onClick={onClose}
        className="mt-4 w-full text-[10px] text-neutral-500 hover:text-white uppercase tracking-widest"
      >
        Listo
      </button>
    </div>
  );
};

export default TempPromoModal;
